import { ApiError, OfflineError, type SurfaceResponse } from '../../data/api'
import { surfaceReportFrom } from '../../lib/surface'
import type { LatLon, SurfaceReport } from '../../lib/types'

/**
 * Den Belag einer Etappe holen - über so viele Aufrufe, wie es braucht.
 *
 * Bewusst kein React in dieser Datei, dieselbe Aufteilung wie bei
 * stageRouting.ts: der Ablauf - wie oft gefragt wird, wann aufgehört wird, was
 * ein Fehler bedeutet - ist die eigentliche Entscheidung und lässt sich so ohne
 * DOM prüfen. Die Oberfläche daneben hält nur den Fortschritt und den Knopf.
 *
 * Warum es überhaupt mehrere Aufrufe sind: der Server schlägt den Belag bei
 * Overpass nach, und das dauert. Eine Etappe von 800 Kilometern zerfällt in elf
 * Blöcke zu je tausend Knotenkennungen, ein Block braucht ein bis acht
 * Sekunden, und der Server hat eine Frist von dreissig. Er beantwortet
 * deshalb, was er schafft, legt jeden geglückten Block ab und nennt in
 * "pending", wie viele fehlen. Der zweite Aufruf holt nur noch den Rest - was
 * schon da ist, kostet nichts mehr.
 */

/**
 * Wie oft höchstens gefragt wird.
 *
 * Zwölf. Die längste Etappe dieser Reise (Bern - Bled, 792 km) hat elf Blöcke,
 * und selbst wenn je Runde nur einer durchkommt, ist sie damit einmal fertig.
 * Eine Grenze muss es trotzdem geben: ohne sie liefe die Schleife bei einem
 * Dienst, der dauerhaft schweigt, endlos weiter und schickte alle paar
 * Sekunden eine Anfrage an einen fremden Server, den gerade jemand anders
 * überlastet.
 */
export const BELAG_MAX_RUNDEN = 12

/**
 * Nach so vielen Runden ohne Fortschritt wird aufgehört.
 *
 * Zwei. Eine einzelne Runde ohne Fortschritt ist der Normalfall bei einem
 * überlasteten Spiegel - gemessen scheiterten drei von fünf Teilabfragen, und
 * beim nächsten Versuch klappte dieselbe in einer Sekunde. Zwei hintereinander
 * heissen dagegen, dass gerade nichts zu holen ist, und dann ist Weiterfragen
 * nur Last für den fremden Dienst.
 *
 * Was schon geholt wurde, bleibt dabei erhalten: der Bericht wird mit der
 * Abdeckung zurückgegeben, die er erreicht hat, und der Nutzer kann später
 * weitermachen. Die Blöcke liegen dann im Speicher des Servers.
 */
export const BELAG_MAX_STILLSTAND = 2

/**
 * Pause zwischen zwei Runden.
 *
 * Kurz, denn die Drosselung sitzt im Server und nicht hier: er hält den
 * Abstand zu Overpass selbst ein, und eine Runde dauert ohnehin Sekunden. Die
 * Pause gibt es trotzdem, damit ein Fehlschlag nicht in derselben Millisekunde
 * wiederholt wird.
 */
export const BELAG_PAUSE_MS = 300

/** Wie weit der Abruf ist. */
export interface SurfaceProgress {
  /** Wie viele Blöcke die Strecke insgesamt hat. */
  blocks: number
  /** Wie viele davon beantwortet sind. */
  done: number
}

/** Wie der Abruf ausgegangen ist. */
export interface SurfaceRunResult {
  /**
   * Was herausgekommen ist, auch wenn es unvollständig blieb.
   *
   * Ein Teilergebnis ist etwas wert und wird deshalb zurückgegeben: es sagt
   * ehrlich, auf wie viel Prozent der Strecke nachgesehen wurde, und die
   * Oberfläche schreibt genau das hin. Null heisst, dass gar nichts zu holen
   * war.
   */
  report: SurfaceReport | null
  /** Wie viele Blöcke am Ende noch offen waren. */
  pending: number
  /** Der Grund, an dem der Abruf hängengeblieben ist, sonst null. */
  error: string | null
  /** Wurde von Hand abgebrochen? */
  aborted: boolean
}

/** Was der Abruf von aussen braucht. Alles einsetzbar, damit er prüfbar bleibt. */
export interface SurfaceRunDeps {
  /** Holt den Belag, üblicherweise ApiClient.fetchSurface. */
  fetchSurface: (
    from: LatLon,
    via: LatLon[],
    to: LatLon,
    signal?: AbortSignal,
  ) => Promise<SurfaceResponse>
  onProgress?: (progress: SurfaceProgress) => void
  now?: () => number
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>
  signal?: AbortSignal
  maxRunden?: number
  pauseMs?: number
}

/**
 * Holt den Belag einer Strecke, bis nichts mehr offen ist.
 *
 * @param from Startkoordinate, via die Zwischenpunkte, to das Ziel - dieselben
 *
 *   drei, mit denen die Strecke berechnet wurde. Ein anderer Satz ergäbe eine
 *   andere Strasse, und deren Belag stünde dann an der falschen Etappe.
 *
 * @returns Das beste Ergebnis, das zustande kam, samt der Zahl der offenen
 *
 *   Blöcke. Ein Fehler in einer späteren Runde verwirft nicht, was die früheren
 *   gebracht haben - er beendet nur den Lauf.
 */
export async function fetchSurfaceReport(
  from: LatLon,
  via: LatLon[],
  to: LatLon,
  deps: SurfaceRunDeps,
): Promise<SurfaceRunResult> {
  const maxRunden = deps.maxRunden ?? BELAG_MAX_RUNDEN
  const pause = deps.pauseMs ?? BELAG_PAUSE_MS
  const sleep = deps.sleep ?? warten
  const now = deps.now ?? Date.now

  let report: SurfaceReport | null = null
  let pending = 0
  let stillstand = 0

  for (let runde = 0; runde < maxRunden; runde++) {
    if (deps.signal?.aborted) return { report, pending, error: null, aborted: true }
    if (runde > 0) {
      await sleep(pause, deps.signal)
      if (deps.signal?.aborted) return { report, pending, error: null, aborted: true }
    }

    let antwort: SurfaceResponse
    try {
      antwort = await deps.fetchSurface(from, via, to, deps.signal)
    } catch (error) {
      if (deps.signal?.aborted) return { report, pending, error: null, aborted: true }
      // Was schon da ist, bleibt: ein Abbruch in der siebten Runde macht die
      // sechs davor nicht ungültig.
      return { report, pending, error: belagFehlerText(error), aborted: false }
    }

    // Ein unbrauchbarer Bericht verwirft nicht, was frühere Runden gebracht
    // haben - sonst hinge das Ergebnis einer Minute Arbeit an der letzten
    // Antwort.
    const frisch = surfaceReportFrom(antwort.report, now())
    if (frisch) report = frisch

    const offen = Number.isFinite(antwort.pending) ? Math.max(0, antwort.pending) : 0
    const bloecke = Number.isFinite(antwort.blocks) ? Math.max(0, antwort.blocks) : 0
    deps.onProgress?.({ blocks: bloecke, done: Math.max(0, bloecke - offen) })

    if (offen === 0) return { report, pending: 0, error: null, aborted: false }

    // Kein Fortschritt: entweder ist der fremde Dienst gerade weg, oder es
    // gibt Blöcke, die sich nicht beantworten lassen. Beides ist kein Grund,
    // ihn weiter zu befragen.
    stillstand = runde > 0 && offen >= pending ? stillstand + 1 : 0
    pending = offen
    if (stillstand >= BELAG_MAX_STILLSTAND) {
      return { report, pending, error: null, aborted: false }
    }
  }

  // Die Runden sind aufgebraucht. Kein Fehler - was da ist, ist da, und der
  // Knopf lässt sich noch einmal drücken.
  return { report, pending, error: null, aborted: false }
}

/**
 * Der Fehler in einem Satz, den man dem Nutzer zeigen kann.
 *
 * Eigene Sätze und nicht die von routeErrorText: dort geht es um eine Strecke,
 * die man ohne Netz gar nicht bekommt, hier um eine Beigabe zu einer Strecke,
 * die schon da ist. „Die Strecke lässt sich nur mit Verbindung berechnen“ wäre
 * an dieser Stelle schlicht falsch - berechnet ist sie längst.
 */
export function belagFehlerText(error: unknown): string {
  if (error instanceof OfflineError) {
    return 'Kein Netz. Der Belag lässt sich nur mit Verbindung nachschlagen; die Strecke bleibt.'
  }
  if (error instanceof ApiError) {
    if (error.isAuthProblem) return 'Die Anmeldung ist abgelaufen. Neu anmelden und noch einmal.'
    if (error.code === 'upstream_unavailable') {
      return 'Die OSM-Abfrage antwortet gerade nicht. Später noch einmal versuchen — was schon geholt wurde, bleibt gespeichert.'
    }
    if (error.code === 'route_unavailable') {
      return 'Ohne Strasse zwischen diesen Punkten gibt es nichts nachzuschlagen.'
    }
    if (error.code === 'bad_request') {
      return 'Mit diesen Koordinaten kann der Dienst nichts anfangen.'
    }
    return `Der Server antwortete mit ${String(error.status)} (${error.code}).`
  }
  return 'Der Belag liess sich nicht nachschlagen.'
}

/**
 * Wartet, lässt sich aber abbrechen.
 *
 * Wortgleich zu pause() in stageRouting.ts und trotzdem hier: die beiden Läufe
 * sind voneinander unabhängig, und eine gemeinsame Hilfsfunktion in einer
 * dritten Datei wäre mehr Kopplung, als sechs Zeilen wert sind.
 */
function warten(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve()
      return
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abbrechen)
      resolve()
    }, ms)
    function abbrechen() {
      clearTimeout(timer)
      resolve()
    }
    signal?.addEventListener('abort', abbrechen, { once: true })
  })
}
